import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Send, X } from 'lucide-react';
import './RequestAccess.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function RequestAccess() {
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    confirmEmail: '',
    howFound: '',
    cuisines: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.fullName.trim() || !form.email.trim() || !form.cuisines.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    if (form.email !== form.confirmEmail) {
      setError('Email addresses do not match.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/public/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Request failed');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="ra-page" data-testid="request-access-page">
        <div className="ra-card">
          <div className="ra-success" data-testid="request-access-success">
            <div className="ra-success-icon">&#x2705;</div>
            <h2>Request Sent!</h2>
            <p>Your access request has been submitted. The admin will review it and set up your account. You'll receive an email once approved.</p>
            <Link to="/" className="ra-btn ra-btn-primary" data-testid="request-access-back-to-login">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ra-page" data-testid="request-access-page">
      <div className="ra-card">
        <div className="ra-header">
          <h2 className="ra-title" data-testid="request-access-title">Request Access</h2>
          <Link to="/" className="ra-close" data-testid="request-access-close">
            <X size={20} />
          </Link>
        </div>
        <p className="ra-subtitle">Fill out this form to request access to track your dining adventures.</p>

        <form onSubmit={handleSubmit} className="ra-form">
          <div className="ra-field">
            <label className="ra-label">Full Name <span className="ra-req">*</span></label>
            <input
              type="text"
              name="fullName"
              className="ra-input"
              placeholder="John Smith"
              value={form.fullName}
              onChange={handleChange}
              data-testid="request-access-fullname"
            />
          </div>

          <div className="ra-field">
            <label className="ra-label">Email Address <span className="ra-req">*</span></label>
            <input
              type="email"
              name="email"
              className="ra-input"
              placeholder="you@example.com"
              value={form.email}
              onChange={handleChange}
              data-testid="request-access-email"
            />
          </div>

          <div className="ra-field">
            <label className="ra-label">Confirm Email <span className="ra-req">*</span></label>
            <input
              type="email"
              name="confirmEmail"
              className="ra-input"
              placeholder="you@example.com"
              value={form.confirmEmail}
              onChange={handleChange}
              data-testid="request-access-confirm-email"
            />
          </div>

          <div className="ra-field">
            <label className="ra-label">How did you find us?</label>
            <input
              type="text"
              name="howFound"
              className="ra-input"
              placeholder="Friend referral, Google search, etc."
              value={form.howFound}
              onChange={handleChange}
              data-testid="request-access-how-found"
            />
          </div>

          <div className="ra-field">
            <label className="ra-label">What cuisines are you interested in? <span className="ra-req">*</span></label>
            <textarea
              name="cuisines"
              className="ra-input ra-textarea"
              placeholder="Italian, Mexican, BBQ, Sushi, etc."
              value={form.cuisines}
              onChange={handleChange}
              rows={3}
              data-testid="request-access-cuisines"
            />
          </div>

          {error && <p className="ra-error" data-testid="request-access-error">{error}</p>}

          <div className="ra-actions">
            <Link to="/" className="ra-btn ra-btn-cancel" data-testid="request-access-cancel">
              Cancel
            </Link>
            <button type="submit" className="ra-btn ra-btn-primary" disabled={loading} data-testid="request-access-submit">
              {loading ? 'Sending...' : (
                <>
                  <Send size={16} />
                  Send Request
                </>
              )}
            </button>
          </div>
        </form>

        <div className="ra-footer-link">
          <Link to="/" data-testid="request-access-signin-link">Already a user? Sign in here</Link>
        </div>
      </div>
    </div>
  );
}
